import React, { useState } from 'react';
import '../styles/Page.css';
import '../styles/Services.css';
import { ChevronDown, ChevronUp, Building2, Shield, Users, Cog } from 'lucide-react';

function Services() {
  const [expandedCategory, setExpandedCategory] = useState(null);

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  const services = [
    {
      id: 1,
      icon: <Building2 size={48} color="#4dd0e1" />,
      title: 'Strategic Execution & Enterprise Problem Solving',
      description: 'Translate strategy into action with execution governance, process optimization, and performance measurement.',
      subcategories: [
        {
          name: 'Strategy-to-Action Translation',
          items: [
            { title: 'Strategic Plan Operationalization', desc: 'Transform strategic plans into actionable roadmaps with clear milestones, ownership, and execution timelines' },
            { title: 'Organizational Alignment', desc: 'Align teams, processes, and resources to strategic objectives with clear governance structures' },
            { title: 'Organizational Design', desc: 'Structure teams and workflows to support strategic priorities and operational excellence' },
            { title: 'Execution Governance', desc: 'Establish decision-making frameworks, cadences, and accountability mechanisms to sustain momentum' }
          ]
        },
        {
          name: 'Enterprise Problem Solving',
          items: [
            { title: 'Cross-Functional Problem Solving', desc: 'Facilitate structured problem-solving across organizational boundaries to address systemic issues' },
            { title: 'Root Cause Analysis', desc: 'Apply rigorous analytical methods to identify and eliminate underlying causes of recurring problems' },
            { title: 'Constraint Identification & Flow Improvement', desc: 'Use Lean Six Sigma and Theory of Constraints to identify bottlenecks and optimize throughput' }
          ]
        },
        {
          name: 'Process & Value Stream Optimization',
          items: [
            { title: 'Process Re-Engineering', desc: 'Redesign end-to-end processes to eliminate waste, reduce cycle time, and improve quality' },
            { title: 'Value Stream Mapping', desc: 'Map current and future state value streams to align processes with customer value and strategic outcomes' }
          ]
        },
        {
          name: 'Program & Initiative Execution',
          items: [
            { title: 'Program Execution Support', desc: 'Provide hands-on execution support for large-scale initiatives requiring discipline and visibility' },
            { title: 'Project Coordination', desc: 'Coordinate across diverse teams and stakeholders to ensure seamless delivery of program goals' }
          ]
        },
        {
          name: 'Performance Measurement & Governance',
          items: [
            { title: 'Performance Measurement & KPIs', desc: 'Design and implement measurement systems, dashboards, and KPIs to track progress and drive accountability' }
          ]
        }
      ]
    },
    {
      id: 2,
      icon: <Shield size={48} color="#4dd0e1" />,
      title: 'Navy & DoD Sustainment Expertise',
      description: 'Expert-level advisory and execution support for Navy and DoD maintenance, sustainment, and industrial operations.',
      subcategories: [
        {
          name: 'Maintenance & Sustainment Systems',
          items: [
            { title: 'Three-Level Maintenance Expertise', desc: 'Apply deep operational knowledge of Organizational, Intermediate, and Depot-level maintenance systems and workflows' },
            { title: 'Sustainment as a System of Systems', desc: 'Integrate maintenance, supply chain, workforce, and technology holistically to support readiness' },
            { title: 'Public Shipyards & Aviation Depots', desc: 'Support public shipyard and aviation depot operations, planning, and throughput optimization' }
          ]
        },
        {
          name: 'Industrial Operations & Execution',
          items: [
            { title: 'Maintenance Planning & Execution', desc: 'Optimize maintenance planning, scheduling, and execution to improve availability and reduce downtime' },
            { title: 'Industrial Throughput Optimization', desc: 'Apply Lean and constraint management methods to increase industrial capacity and flow' },
            { title: 'Availability Execution Support', desc: 'Provide hands-on support for complex maintenance availabilities and industrial work packages' }
          ]
        },
        {
          name: 'Policy & Integration Support',
          items: [
            { title: 'Policy-to-Execution Translation', desc: 'Bridge DoD policy guidance and operational reality to enable effective implementation' },
            { title: 'Program Recovery & Flow Improvement', desc: 'Diagnose and resolve sustainment program performance issues and execution bottlenecks' },
            { title: 'Integration with Primes, OEMs & Technology Providers', desc: 'Facilitate alignment and collaboration between government, industry, and technology partners' }
          ]
        }
      ]
    },
    {
      id: 3,
      icon: <Cog size={48} color="#4dd0e1" />,
      title: 'Enabling Capabilities: Technology, Data & Training',
      description: 'Enable and sustain execution through digital modernization, data analytics, process automation, and leadership development.',
      subcategories: [
        {
          name: 'Digital & IT Modernization',
          items: [
            { title: 'Digital & IT Modernization Planning', desc: 'Develop comprehensive roadmaps to guide organizations through digital modernization and technology adoption' },
            { title: 'Agile & Digital Transformation Roadmaps', desc: 'Enable Agile methodologies and digital transformation to increase delivery speed, adaptability, and team collaboration' }
          ]
        },
        {
          name: 'Cybersecurity & Data Analytics',
          items: [
            { title: 'Cybersecurity Risk Management & Compliance', desc: 'Assess cybersecurity risks and implement policies, controls, and compliance measures to mitigate threats' },
            { title: 'Data Analytics, Measurement Systems & Dashboards', desc: 'Apply data analytics and build measurement frameworks with dashboards, KPIs, and visualization tools' }
          ]
        },
        {
          name: 'Process Automation & Documentation',
          items: [
            { title: 'Process Documentation & Automation (RPA)', desc: 'Document and map processes, then implement RPA solutions to automate repetitive tasks and improve efficiency' }
          ]
        },
        {
          name: 'Leadership & Training',
          items: [
            { title: 'Leadership Coaching & Assessments', desc: 'Provide coaching and assessments to develop leadership capabilities and identify organizational culture dynamics' },
            { title: 'Lean Six Sigma & Theory of Constraints Training', desc: 'Deliver LSS and TOC training programs to build organizational capability in constraint management and optimization' },
            { title: 'Problem-Solving & Execution Workshops', desc: 'Facilitate hands-on workshops to develop critical thinking, structured problem-solving, and execution discipline' }
          ]
        }
      ]
    },
    {
      id: 4,
      icon: <Users size={48} color="#4dd0e1" />,
      title: 'Connector & Ecosystem Services',
      description: 'Partner identification, ecosystem alignment, and integrated execution support to accelerate mission outcomes.',
      subcategories: [
        {
          name: 'Partner Identification & Vetting',
          items: [
            { title: 'Capability & Partner Identification', desc: 'Identify and vet organizations, technologies, and capabilities aligned with mission needs and problem contexts' },
            { title: 'Due Diligence & Assessment', desc: 'Conduct rigorous assessments of potential partners to ensure technical fit, cultural alignment, and execution readiness' }
          ]
        },
        {
          name: 'Ecosystem Alignment & Collaboration',
          items: [
            { title: 'Government–Industry–Academia Alignment', desc: 'Facilitate collaboration across government, industry, and academic sectors to address complex challenges' },
            { title: 'Collaboration Model & Governance Design', desc: 'Design governance structures, engagement models, and collaboration frameworks for multi-party initiatives' },
            { title: 'Pilot & Prototype Enablement', desc: 'Support rapid piloting and prototyping efforts to validate concepts and accelerate adoption decisions' }
          ]
        },
        {
          name: 'Integrated Execution Support',
          items: [
            { title: 'Integrated Execution Across Partners', desc: 'Coordinate and integrate execution across multiple partners to ensure alignment, accountability, and outcomes' },
            { title: 'Ethical Stewardship & Outcome Accountability', desc: 'Serve as an ethical steward ensuring alignment, effective execution, and outcomes that responsibly address real problems' }
          ]
        }
      ]
    }
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1>How We Serve</h1>
        <p>Strategic Execution | Enterprise Problem Solving | Sustainment Expertise</p>
      </div>

      <div className="page-content">
        {/* Company Overview */}
        <section className="content-section company-overview">
          <p>
            Our capabilities are organized into strategic pillars that address distinct organizational challenges. Each pillar represents a cohesive set of services that can be engaged independently or integrated into comprehensive transformation initiatives, designed to align with task orders, proposals, and phased execution models.
          </p>
        </section>
      </div>

      {/* Strategic Framework - Full Width */}
      <section className="strategic-framework">
          <h2 className="framework-title">Service Offerings</h2>
          <p className="framework-intro">
            The following pillars provide a high-level overview of our core service areas and the organizational challenges they address. For detailed task-level capabilities, deliverables, and engagement options, see the comprehensive services catalog below.
          </p>

          {/* Core Offering Pillar */}
          <div className="strategic-pillar">
            <div className="pillar-header">
              <h3>Strategic → Operational Execution & Enterprise Problem Solving</h3>
              <p className="pillar-purpose">Enable organizations to translate strategy into execution while solving the systemic problems that limit performance.</p>
            </div>
            <div className="pillar-outcomes">
              <h4>What We Solve</h4>
              <ul>
                <li>Strategic plans that stall during execution</li>
                <li>Chronic performance or delivery gaps</li>
                <li>Repeated problem recurrence across functions</li>
                <li>Large initiatives requiring execution discipline and visibility</li>
              </ul>
            </div>
            <p className="pillar-reference">
              <em>Detailed capabilities supporting this pillar include strategic planning, organizational alignment, process re-engineering, program management, and performance measurement—see task-level services below.</em>
            </p>
          </div>

          {/* Sustainment Expertise Pillar */}
          <div className="strategic-pillar">
            <div className="pillar-header">
              <h3>Navy & DoD Sustainment Expertise</h3>
              <p className="pillar-purpose">Expert-level advisory and execution support grounded in career-long federal experience within Navy and DoD sustainment systems.</p>
            </div>
            <div className="pillar-domain">
              <h4>Domain Specialization</h4>
              <p>Deep operational knowledge of the three levels of maintenance (Organizational, Intermediate, Depot), sustainment as a system of systems, public shipyards, aviation depots, industrial maintenance environments, and policy-to-execution translation within DoD contexts.</p>
            </div>
            <div className="pillar-engagement">
              <h4>How We Engage</h4>
              <ul>
                <li>Independent sustainment assessments</li>
                <li>Targeted advisory support</li>
                <li>Execution support for sustainment initiatives</li>
                <li>Program recovery and flow improvement</li>
                <li>Integration support with primes, OEMs, and technology providers</li>
              </ul>
            </div>
            <p className="pillar-reference">
              <em>This domain expertise is applied across our business transformation, process optimization, and program management services.</em>
            </p>
          </div>

          {/* Enabling Capabilities */}
          <div className="strategic-pillar enabling">
            <div className="pillar-header">
              <h3>Enabling Capabilities</h3>
              <p className="pillar-purpose">Technology, data, training, and leadership enablement to support and sustain execution.</p>
            </div>
            <p className="pillar-context">
              These capabilities—including digital modernization, cybersecurity, data analytics, process automation, leadership coaching, and Lean Six Sigma training—are typically deployed in support of core execution and problem-solving efforts.
            </p>
            <p className="pillar-reference">
              <em>See IT & Cybersecurity Services, Training & Leadership Development, and Process & Technology Consulting below for detailed offerings.</em>
            </p>
          </div>

          {/* Connector & Ecosystem Services */}
          <div className="strategic-pillar connector">
            <div className="pillar-header">
              <h3>Connector & Ecosystem Services</h3>
              <p className="pillar-purpose">Accelerate execution by aligning the right organizations, capabilities, and technologies around real problems.</p>
            </div>
            <div className="pillar-connector">
              <h4>What We Do</h4>
              <ul>
                <li>Partner identification and vetting</li>
                <li>Government–industry–academia alignment</li>
                <li>Ecosystem and collaboration model design</li>
                <li>Pilot and prototype enablement</li>
                <li>Integrated execution support across partners</li>
              </ul>
            </div>
            <div className="pillar-distinction">
              <h4>Key Distinction</h4>
              <p>
                <strong>We are not a broker or referral service.</strong> We are not transactional introducers or resellers. 
                One Aligned serves as an ethical steward for our clients, the broader community, and the nation—engaged to ensure alignment, effective execution, and outcomes that responsibly address real problems and close existing gaps.
              </p>
            </div>
          </div>
        </section>

      <div className="page-content">
        {/* Divider before detailed services */}
        <div className="services-divider">
          <h2>Detailed Capabilities Catalog</h2>
          <p>Task-level services organized by functional area. Click each category to explore specific capabilities.</p>
        </div>

        <section className="services-catalog">
          {services.map((service) => (
            <div key={service.id} className="service-category">
              <div 
                className="service-category-header"
                onClick={() => toggleCategory(service.id)}
              >
                <div className="service-category-title">
                  <div className="service-category-icon">{service.icon}</div>
                  <div>
                    <h2>{service.title}</h2>
                    <p className="service-category-desc">{service.description}</p>
                  </div>
                </div>
                <button className="expand-button">
                  {expandedCategory === service.id ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                </button>
              </div>

              {expandedCategory === service.id && (
                <div className="service-category-content">
                  {service.subcategories.map((subcategory, idx) => (
                    <div key={idx} className="service-subcategory">
                      <h3 className="subcategory-title">{subcategory.name}</h3>
                      <div className="service-items">
                        {subcategory.items.map((item, itemIdx) => (
                          <div key={itemIdx} className="service-item">
                            <h4>{item.title}</h4>
                            <p>{item.desc}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}

export default Services;
